/**
 * Aba "Curva de Mercado" da Mesa de Operacoes (Task #50 Fase B + #68).
 *
 * Layout pos-#68:
 *   1. Header com titulo + indicador "Atualizado ha Nh".
 *   2. Grafico de linha (CurvaMercadoChart) com pills de submercado/tipo.
 *   3. Tabela plana com 1 linha por janela retornada pelo endpoint
 *      /api/curva-forward (default ja filtra ind_oficial=true via #68).
 *
 * Filtro "Apenas oficial" foi removido em #68: backend retorna so oficiais
 * por default; trocar pra UI (escape via ?incluir_nao_oficial=true) ficou
 * pra eventual debug futuro.
 *
 * Empty state: orienta o operador a rodar
 * `python scripts/zeus_curva_forward_sync.py` via run-task.
 */
import { AlertTriangle } from "lucide-react";
import { useCurvaForward } from "../../hooks/useCurvaForward";
import { useLastSync } from "../../hooks/useLastSync";
import { useZeusStatus } from "../../hooks/useZeusStatus";
import { CurvaStaleBadge } from "../ui/CurvaStaleBadge";
import { parseUtcNaive } from "../../utils/dateBr";
import type { CurvaForwardRow } from "../../api/types";
import { CurvaMercadoChart } from "./CurvaMercadoChart";
import { ExportDropdown, type ExportColumn } from "../ui/ExportDropdown";
import { dateCell, num } from "../../utils/xlsxHelpers";
import { PLD_LIMITS_2026, isOutOfPldRange, pldRangeForYear } from "../../utils/pldLimits";

function fmtDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

/** #20: último dia do mês da data ISO ("2026-07-01" → "2026-07-31"; fev
 * bissexto ok — `new Date(y, m, 0)` = último dia do mês m 1-based). SÓ em
 * EXIBIÇÃO (export + colunas Fim/Período em tela): o dt_fim interno segue
 * 01/mm (convenção Zeus #84/#35) — alimenta derivação de bloco e o loader
 * do MtM (#220). */
function _ultimoDiaDoMes(iso: string): string {
  const [y, m] = iso.split("-").map(Number);
  const dia = new Date(y, m, 0).getDate();
  return `${iso.slice(0, 7)}-${String(dia).padStart(2, "0")}`;
}

/** dd/mm/yyyy do ÚLTIMO dia do mês de `iso` — atalho de exibição do Fim. */
function fmtDateFim(iso: string): string {
  return fmtDate(_ultimoDiaDoMes(iso));
}

function fmtNum(v: string | null): string {
  if (v === null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const COLUMNS: Array<{ key: keyof CurvaForwardRow; label: string }> = [
  { key: "forward_se", label: "Fwd SE" },
  { key: "agio_se", label: "Ágio SE" },
  { key: "swap_s", label: "Swap S" },
  { key: "swap_ne", label: "Swap NE" },
  { key: "swap_n", label: "Swap N" },
  { key: "swap_i0", label: "I0%" },
  { key: "swap_i5", label: "I50%" },
  { key: "swap_i1", label: "I100%" },
  { key: "swap_i8", label: "I80%" },
  { key: "swap_cq5", label: "CQ5" },
  { key: "swap_cq1", label: "CQ1" },
];

// --- Export Excel (Task #67) ----------------------------------------------
// Reusa ExportDropdown + xlsxHelpers da #74. Datas viram celulas Excel-native
// (dateCell), precos em R$/MWh viram num com 2 casas — Mesa pode SUM/AVG no
// destino e pivotear. Coluna "Oficial" omitida no .xlsx: hoje 100% das janelas
// em prod sao oficiais (filtro removido em #68); coluna so polui o export.
type CurvaExport = CurvaForwardRow & Record<string, unknown>;

const EXPORT_COLUMNS: ExportColumn<CurvaExport>[] = [
  { key: "dt_inicio", header: "Início", format: (r) => dateCell(r.dt_inicio) },
  { key: "dt_fim", header: "Fim", format: (r) => dateCell(_ultimoDiaDoMes(r.dt_fim)) },
  { key: "forward_se", header: "Fwd SE (R$/MWh)", format: (r) => num(_toNumOrNull(r.forward_se), 2) },
  { key: "agio_se", header: "Ágio SE (R$/MWh)", format: (r) => num(_toNumOrNull(r.agio_se), 2) },
  { key: "swap_s", header: "Swap S (R$/MWh)", format: (r) => num(_toNumOrNull(r.swap_s), 2) },
  { key: "swap_ne", header: "Swap NE (R$/MWh)", format: (r) => num(_toNumOrNull(r.swap_ne), 2) },
  { key: "swap_n", header: "Swap N (R$/MWh)", format: (r) => num(_toNumOrNull(r.swap_n), 2) },
  { key: "swap_i0", header: "I0% (R$/MWh)", format: (r) => num(_toNumOrNull(r.swap_i0), 2) },
  { key: "swap_i5", header: "I50% (R$/MWh)", format: (r) => num(_toNumOrNull(r.swap_i5), 2) },
  { key: "swap_i1", header: "I100% (R$/MWh)", format: (r) => num(_toNumOrNull(r.swap_i1), 2) },
  { key: "swap_i8", header: "I80% (R$/MWh)", format: (r) => num(_toNumOrNull(r.swap_i8), 2) },
  { key: "swap_cq5", header: "CQ5 (R$/MWh)", format: (r) => num(_toNumOrNull(r.swap_cq5), 2) },
  { key: "swap_cq1", header: "CQ1 (R$/MWh)", format: (r) => num(_toNumOrNull(r.swap_cq1), 2) },
];

function _toNumOrNull(v: string | null): number | null {
  if (v === null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// --- Validacao PLD (Task #65) ---------------------------------------------
// Preco efetivo Convencional = forward_se + agio_se + swap_<sub>. Se cair
// fora de [PLDmin, PLDmax] do ano regulatorio (ANEEL), marca a celula do
// swap correspondente com bg ambar + tooltip pra Mesa investigar. Sem
// validacao em I0/I5/I100/CQ — ranges proprios, Mesa precisa definir
// limites antes (#65 cobriu so Convencional).

/** Mapping coluna swap → key do CurvaForwardRow do submercado. */
const SUBMERCADO_SWAP_COLS: Record<"forward_se" | "swap_s" | "swap_ne" | "swap_n", string> = {
  forward_se: "SE",  // marca celula Fwd SE quando efetivo SE Convencional fora
  swap_s: "S",
  swap_ne: "NE",
  swap_n: "N",
};

function _calcularPrecoConvencional(
  row: CurvaForwardRow,
  submercado: "SE" | "S" | "NE" | "N",
): number | null {
  const fwd = _toNumOrNull(row.forward_se);
  if (fwd === null) return null;
  const agio = _toNumOrNull(row.agio_se) ?? 0;
  let swap = 0;
  if (submercado !== "SE") {
    const col = submercado === "S" ? "swap_s"
              : submercado === "NE" ? "swap_ne"
              : "swap_n";
    swap = _toNumOrNull(row[col] as string | null) ?? 0;
  }
  return fwd + agio + swap;
}

/** Para uma celula da tabela (row × coluna), retorna preco efetivo
 * Convencional do submercado ASSOCIADO se a coluna for de submercado
 * (Fwd SE / Swap S/NE/N). Outras colunas (agio_se, swap_i*, swap_cq*)
 * retornam null — nao validamos. */
function _precoConvencionalDaCelula(
  row: CurvaForwardRow, columnKey: keyof CurvaForwardRow,
): number | null {
  const sub = SUBMERCADO_SWAP_COLS[columnKey as keyof typeof SUBMERCADO_SWAP_COLS];
  if (!sub) return null;
  return _calcularPrecoConvencional(row, sub as "SE" | "S" | "NE" | "N");
}

function _anoRefDaJanela(row: CurvaForwardRow): number {
  // Ano de referencia = ano de dt_inicio. Pra janelas trans-ano (raras),
  // usamos o ano de inicio — ANEEL fixa por ano regulatorio fechado.
  return parseInt(row.dt_inicio.slice(0, 4), 10);
}

/** True se a celula deve receber warning de PLD fora do range. */
function _celulaForaDoPld(
  row: CurvaForwardRow, columnKey: keyof CurvaForwardRow,
): boolean {
  const preco = _precoConvencionalDaCelula(row, columnKey);
  if (preco === null) return false;
  return isOutOfPldRange(preco, _anoRefDaJanela(row));
}

/** Conta quantas linhas tem ao menos 1 submercado Convencional fora do
 * range PLD do ano. Alimenta badge agregado no header da tabela. */
function _contarLinhasComWarningPld(rows: CurvaForwardRow[]): number {
  return rows.filter((row) => {
    const ano = _anoRefDaJanela(row);
    return (["SE", "S", "NE", "N"] as const).some((sub) => {
      const preco = _calcularPrecoConvencional(row, sub);
      return preco !== null && isOutOfPldRange(preco, ano);
    });
  }).length;
}

// --- #24: produtos de BLOCO (dt_inicio≠dt_fim) com rótulo de mercado ---------
// Convenção Zeus (#84/#35): dt_fim = 1º dia do ÚLTIMO mês do período. O span em
// meses é inclusivo. Mensal = dt_inicio==dt_fim (NÃO entra aqui).
function _mesesSpanBloco(ini: string, fim: string): number {
  const [yi, mi] = ini.split("-").map(Number);
  const [yf, mf] = fim.split("-").map(Number);
  return (yf - yi) * 12 + (mf - mi) + 1;
}

/** Nome de mercado do bloco a partir de [dt_inicio..dt_fim]: "4º tri/26",
 * "1º sem/26", "Anual 2027", "2033–2035" ou fallback bruto. */
function rotuloBloco(ini: string, fim: string): string {
  const [yi, mi] = ini.split("-").map(Number);
  const [yf, mf] = fim.split("-").map(Number);
  const span = _mesesSpanBloco(ini, fim);
  const yy = String(yi).slice(2);
  if (yi === yf) {
    if (span === 3 && [1, 4, 7, 10].includes(mi)) return `${Math.floor((mi - 1) / 3) + 1}º tri/${yy}`;
    if (span === 6 && (mi === 1 || mi === 7)) return `${mi === 1 ? 1 : 2}º sem/${yy}`;
    if (span === 12 && mi === 1) return `Anual ${yi}`;
    return `${ini.slice(5, 7)}–${fim.slice(5, 7)}/${yy}`;
  }
  if (mi === 1 && mf === 12) return `${yi}–${yf}`; // ex.: 2033–2035 (multi-ano)
  return `${fmtDate(ini)}–${fmtDateFim(fim)}`;
}

const BLOCO_COLS: Array<{ key: keyof CurvaForwardRow; label: string }> = [
  { key: "forward_se", label: "Fwd SE" },
  { key: "swap_s", label: "Swap S" },
  { key: "swap_ne", label: "Swap NE" },
  { key: "swap_n", label: "Swap N" },
];

// --- #52: bloco DERIVADO dos mensais quando o produto de bloco não existe ----
// Quando um bloco de mercado padrão (tri/sem) NÃO foi publicado pelo Zeus mas há
// os mensais cobrindo o período, deriva o preço do bloco = MÉDIA PONDERADA POR
// HORAS dos mensais (definição correta de preço de bloco — sem viés por meses de
// tamanhos diferentes). SÓ display na visão de blocos: NÃO toca o MtM (#220, que
// usa os mensais direto via backend) nem os blocos publicados. Anuais vêm do
// Zeus (não derivamos). Guarda: período sem TODOS os mensais (forward_se) não
// deriva — não inventa.
const _MES_ABBR = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const _BLOCO_DERIV_COLS = ["forward_se", "swap_s", "swap_ne", "swap_n"] as const;

/** Horas no mês (dias × 24). `new Date(y, m, 0)` = último dia do mês m (1-based). */
function _horasNoMes(year: number, month1: number): number {
  return new Date(year, month1, 0).getDate() * 24;
}

// Períodos de bloco padrão de um ano: 4 trimestres + 2 semestres (1-based,
// inclusivo). Convenção Zeus (#84/#35): dt_fim = 1º dia do ÚLTIMO mês.
const _PERIODOS_PADRAO: Array<{ startM: number; endM: number }> = [
  { startM: 1, endM: 3 }, { startM: 4, endM: 6 }, { startM: 7, endM: 9 }, { startM: 10, endM: 12 },
  { startM: 1, endM: 6 }, { startM: 7, endM: 12 },
];

export interface BlocoDerivado {
  dt_inicio: string;
  dt_fim: string;
  forward_se: string | null;
  swap_s: string | null;
  swap_ne: string | null;
  swap_n: string | null;
  mesesLabel: string; // "jul–set"
}

/** Deriva os blocos padrão (tri/sem) AUSENTES (não publicados) mas com os
 * mensais cobrindo, via média ponderada por horas. Pura — não muta nada. */
function _derivarBlocosAusentes(rows: CurvaForwardRow[]): BlocoDerivado[] {
  const mensais = new Map<string, CurvaForwardRow>(); // "YYYY-MM" → mensal
  const anos = new Set<number>();
  const publicados = new Set<string>(); // "dt_inicio|dt_fim" dos blocos publicados
  for (const r of rows) {
    if (r.dt_inicio === r.dt_fim) {
      mensais.set(r.dt_inicio.slice(0, 7), r);
      anos.add(Number(r.dt_inicio.slice(0, 4)));
    } else {
      publicados.add(`${r.dt_inicio}|${r.dt_fim}`);
    }
  }
  const out: BlocoDerivado[] = [];
  for (const year of [...anos].sort()) {
    for (const { startM, endM } of _PERIODOS_PADRAO) {
      const dt_inicio = `${year}-${String(startM).padStart(2, "0")}-01`;
      const dt_fim = `${year}-${String(endM).padStart(2, "0")}-01`;
      if (publicados.has(`${dt_inicio}|${dt_fim}`)) continue; // já existe publicado → não deriva
      // Cobertura: TODOS os meses do período com mensal + forward_se válido.
      const meses: CurvaForwardRow[] = [];
      let coberto = true;
      for (let m = startM; m <= endM; m++) {
        const r = mensais.get(`${year}-${String(m).padStart(2, "0")}`);
        if (!r || r.forward_se === null || r.forward_se === "" || !Number.isFinite(Number(r.forward_se))) {
          coberto = false;
          break;
        }
        meses.push(r);
      }
      if (!coberto) continue; // guarda: não inventa
      const derived: BlocoDerivado = {
        dt_inicio, dt_fim,
        forward_se: null, swap_s: null, swap_ne: null, swap_n: null,
        mesesLabel: `${_MES_ABBR[startM - 1]}–${_MES_ABBR[endM - 1]}`,
      };
      for (const col of _BLOCO_DERIV_COLS) {
        let numer = 0;
        let denom = 0;
        for (const r of meses) {
          const v = r[col];
          if (v === null || v === "") continue;
          const n = Number(v);
          if (!Number.isFinite(n)) continue;
          const [yy, mm] = r.dt_inicio.split("-").map(Number);
          const h = _horasNoMes(yy, mm);
          numer += n * h;
          denom += h;
        }
        derived[col] = denom > 0 ? String(numer / denom) : null;
      }
      out.push(derived);
    }
  }
  return out.sort((a, b) => a.dt_inicio.localeCompare(b.dt_inicio) || a.dt_fim.localeCompare(b.dt_fim));
}

interface BlocoDisplay {
  dt_inicio: string;
  dt_fim: string;
  forward_se: string | null;
  swap_s: string | null;
  swap_ne: string | null;
  swap_n: string | null;
  derivado: boolean;
  mesesLabel?: string;
}

// Exportado pra teste.
export const __test__ = { _derivarBlocosAusentes, _horasNoMes, _ultimoDiaDoMes, EXPORT_COLUMNS };

export function CurvaMercadoTab() {
  const { data, isLoading, error } = useCurvaForward();
  const lastSync = useLastSync();
  const zeus = useZeusStatus();

  const curvaSync = lastSync.data?.by_source["zeus_curva_forward"] ?? null;
  // #40: as-of do DADO (nao a idade do cron). O badge so acende com 2+ dias
  // uteis de defasagem (incidente-curva-0907); D-1 legitimo nao acende.
  const curvaDs = zeus.data?.datasets.curva_forward ?? null;
  // #24: produtos de bloco (tri/sem/ano) = dt_inicio≠dt_fim, separados dos
  // vértices mensais (dt_inicio==dt_fim).
  // #52: além dos publicados, deriva os blocos padrão (tri/sem) AUSENTES mas
  // com mensais (média ponderada por horas), rotulados "derivado" e visualmente
  // distintos. Display-only — não afeta MtM (#220) nem os publicados.
  const blocosPublicados: BlocoDisplay[] = data
    .filter((r) => r.dt_inicio !== r.dt_fim)
    .map((r) => ({
      dt_inicio: r.dt_inicio, dt_fim: r.dt_fim,
      forward_se: r.forward_se, swap_s: r.swap_s, swap_ne: r.swap_ne, swap_n: r.swap_n,
      derivado: false,
    }));
  const blocosDerivados: BlocoDisplay[] = _derivarBlocosAusentes(data).map((d) => ({
    dt_inicio: d.dt_inicio, dt_fim: d.dt_fim,
    forward_se: d.forward_se, swap_s: d.swap_s, swap_ne: d.swap_ne, swap_n: d.swap_n,
    derivado: true, mesesLabel: d.mesesLabel,
  }));
  const blocos: BlocoDisplay[] = [...blocosPublicados, ...blocosDerivados].sort(
    (a, b) => a.dt_inicio.localeCompare(b.dt_inicio) || a.dt_fim.localeCompare(b.dt_fim),
  );
  // Task #65: contagem de janelas com pelo menos 1 submercado Convencional
  // efetivo fora do range PLD ANEEL — alimenta badge agregado no header.
  const linhasComWarningPld = _contarLinhasComWarningPld(data);

  if (isLoading) {
    return (
      <div className="text-white/40 text-sm py-12 text-center" data-testid="curva-loading">
        Carregando curva de mercado…
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div
        className="rounded-2xl border border-white/10 bg-indra-azul-surface p-8 text-center"
        data-testid="curva-empty"
      >
        <h3 className="text-white font-semibold mb-2">Sem dados de curva forward</h3>
        <p className="text-white/60 text-sm mb-3">
          {error
            ? "Erro ao carregar a curva. Tente recarregar a página."
            : "A tabela canonical_curva_forward está vazia."}
        </p>
        <p className="text-white/40 text-xs">
          Para popular, rode (uma vez) via run-task:
          <br />
          <code className="text-indra-turquesa">
            python scripts/zeus_curva_forward_sync.py
          </code>
        </p>
      </div>
    );
  }

  return (
    <div data-testid="curva-mercado-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-white font-semibold">Curva de Mercado — Zeus</h2>
          <p className="text-white/50 text-xs mt-1">
            Janelas de fornecimento com preço base SE + swaps por submercado e
            tipo (R$/MWh).
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <CurvaStaleBadge stale={curvaDs?.curva_stale} asOf={curvaDs?.data_ref_zeus} />
          {linhasComWarningPld > 0 && (
            <span
              className="flex items-center gap-1 text-amber-300/90 font-semibold"
              title={
                `Range PLD ANEEL ${PLD_LIMITS_2026.min.toFixed(2).replace(".", ",")}` +
                ` a R$ ${PLD_LIMITS_2026.max.toFixed(2).replace(".", ",")}/MWh. ` +
                `Verificar com Mesa o cadastro destas janelas.`
              }
              data-testid="curva-pld-warning-header"
            >
              <AlertTriangle size={12} />
              {linhasComWarningPld} {linhasComWarningPld === 1 ? "linha" : "linhas"} fora do PLD
            </span>
          )}
          {curvaSync && (
            <span
              className="text-white/40"
              title={`Última materialização: ${parseUtcNaive(curvaSync.started_at).toLocaleString("pt-BR")}`}
            >
              Atualizado há {curvaSync.hours_since.toFixed(0)}h
            </span>
          )}
          <ExportDropdown<CurvaExport>
            filename={`cortex-curva-mercado-${new Date().toISOString().slice(0, 10).replace(/-/g, "")}`}
            primarySheetName="Curva de Mercado"
            columns={EXPORT_COLUMNS}
            rows={data as unknown as CurvaExport[]}
          />
        </div>
      </div>

      <CurvaMercadoChart data={data} />

      {/* #24: produtos de BLOCO de mercado (tri/sem/ano), rotulados, separados
          dos vértices mensais. É a marcação de bloco que a Mesa usa. */}
      {blocos.length > 0 && (
        <div className="mb-6" data-testid="curva-blocos-section">
          <h3 className="text-white font-semibold text-sm mb-1">
            Produtos de bloco — marcação de mercado
          </h3>
          <p className="text-white/50 text-xs mb-2">
            Produtos de período (trimestre/semestre/ano) — janelas com
            dt_início ≠ dt_fim. Distinto dos vértices mensais (R$/MWh).
          </p>
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-sm">
              <thead className="bg-indra-azul-surface text-white/60 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left">Produto</th>
                  <th className="px-3 py-2 text-left">Período</th>
                  {BLOCO_COLS.map((c) => (
                    <th key={c.key} className="px-3 py-2 text-right">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {blocos.map((row) => {
                  const cols = row as unknown as Record<string, string | null>;
                  return (
                    <tr
                      key={`bloco-${row.dt_inicio}-${row.dt_fim}`}
                      className={
                        "border-t border-white/5 hover:bg-white/5 " +
                        (row.derivado ? "italic bg-indra-turquesa/5" : "")
                      }
                      data-testid={row.derivado ? "curva-bloco-derivado-row" : "curva-bloco-row"}
                    >
                      <td
                        className={
                          "px-3 py-2 " + (row.derivado ? "text-white/70" : "text-white/90 font-semibold")
                        }
                      >
                        {rotuloBloco(row.dt_inicio, row.dt_fim)}
                        {row.derivado && (
                          <span
                            className="ml-2 not-italic text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-indra-turquesa/15 text-indra-turquesa align-middle"
                            title="Bloco não publicado pelo Zeus — derivado da média ponderada por horas dos mensais do período. Não é produto de mercado real; o MtM usa os mensais direto."
                          >
                            derivado · média {row.mesesLabel}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-white/60">
                        {fmtDate(row.dt_inicio)} – {fmtDateFim(row.dt_fim)}
                      </td>
                      {BLOCO_COLS.map((c) => (
                        <td key={c.key} className="px-3 py-2 text-right font-mono text-white/80">
                          {fmtNum(cols[c.key as string])}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-indra-azul-surface text-white/60 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">Início</th>
              <th className="px-3 py-2 text-left">Fim</th>
              {COLUMNS.map((c) => (
                <th key={c.key} className="px-3 py-2 text-right">
                  {c.label}
                </th>
              ))}
              <th className="px-3 py-2 text-center">Oficial</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => {
              const ano = _anoRefDaJanela(row);
              const range = pldRangeForYear(ano);
              return (
                <tr
                  key={`${row.dt_inicio}-${row.dt_fim}`}
                  className="border-t border-white/5 hover:bg-white/5"
                  data-testid="curva-row"
                >
                  <td className="px-3 py-2 text-white/90">{fmtDate(row.dt_inicio)}</td>
                  <td className="px-3 py-2 text-white/90">{fmtDateFim(row.dt_fim)}</td>
                  {COLUMNS.map((c) => {
                    const fora = _celulaForaDoPld(row, c.key);
                    const sub = SUBMERCADO_SWAP_COLS[c.key as keyof typeof SUBMERCADO_SWAP_COLS];
                    const tooltip = fora && range
                      ? `Preço efetivo Convencional ${sub} fora do range ` +
                        `PLD ANEEL ${ano} [R$ ${range.min.toFixed(2).replace(".", ",")}; ` +
                        `R$ ${range.max.toFixed(2).replace(".", ",")}]. ` +
                        `Verificar com Mesa.`
                      : undefined;
                    return (
                      <td
                        key={c.key}
                        className={
                          "px-3 py-2 text-right font-mono " +
                          (fora
                            ? "bg-amber-400/15 text-amber-200 cursor-help"
                            : "text-white/80")
                        }
                        title={tooltip}
                        data-testid={fora ? "curva-cell-pld-warning" : undefined}
                      >
                        {fmtNum(row[c.key] as string | null)}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-center">
                    {row.ind_oficial ? (
                      <span className="text-emerald-300 text-xs">✓</span>
                    ) : (
                      <span className="text-white/30 text-xs">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
